/**
 * ESL Chat widget — entry point. Built to one self-contained IIFE (dist/esl-chat.js).
 */

import frame from "./frame.css?inline";
import interior from "./interior.css?inline";
import { createCallout } from "./callout";
import { Chat } from "./chat";
import { IS_DEVELOPMENT, isAuthorizedDomain, readConfig } from "./config";
import { createWidgetDom } from "./dom";
import { bindWindow } from "./interactions";
import { ReCaptcha } from "./recaptcha";

const MOUNTED_FLAG = "eslChatMounted";

// Must be read synchronously: currentScript is null once this script finishes running.
const config = readConfig(document.currentScript);

function showFatal(message: string): void {
  const box = document.createElement("div");
  Object.assign(box.style, {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    zIndex: "9999",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: "15px",
    backgroundColor: "#f8d7da",
    color: "#721c24",
    border: "1px solid #f5c6cb",
    borderRadius: "8px",
  });
  const title = document.createElement("strong");
  title.textContent = "Chat Widget Error";
  box.append(title, document.createElement("br"), message);
  document.body.append(box);
}

function mount(): void {
  if (document.documentElement.dataset[MOUNTED_FLAG]) return;
  document.documentElement.dataset[MOUNTED_FLAG] = "true";

  if (location.protocol !== "https:" && !IS_DEVELOPMENT) {
    showFatal("Chat widget requires HTTPS connection");
    return;
  }
  if (!isAuthorizedDomain()) {
    showFatal("Chat service not available on this domain");
    return;
  }

  const style = document.createElement("style");
  style.id = "chat-widget-styles";
  style.textContent = frame + interior;
  document.head.append(style);
  if (config.logoUrl) {
    document.documentElement.style.setProperty("--esl-chat-logo", `url(${JSON.stringify(config.logoUrl)})`);
  }

  const ui = createWidgetDom();
  const callout = createCallout(ui.buttonContainer, ui.button);
  const recaptcha = config.recaptchaSiteKey ? new ReCaptcha(config.recaptchaSiteKey) : null;
  const chat = new Chat(ui, config, recaptcha);

  bindWindow(ui, callout, () => {
    chat.scrollToBottom(true);
    // Load reCAPTCHA on first open so the first message doesn't wait for the script.
    recaptcha?.load().catch((error: Error) => chat.notice(error.message, "error"));
  });
  ui.inputArea.addEventListener("submit", (e) => {
    e.preventDefault();
    void chat.send(ui.input.value);
  });
  ui.newChatBtn.addEventListener("click", () => chat.newConversation());
  ui.endChatBtn.addEventListener("click", () => chat.endChat());
  for (const button of ui.welcomeScreen.querySelectorAll<HTMLButtonElement>(".chat-widget-action-button")) {
    button.addEventListener("click", () => chat.handleAction(button.dataset.action ?? ""));
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mount);
} else {
  mount();
}
